/**
 * Aura Share Certificate — 화면 값 → Word 템플릿 변수.
 *   템플릿은 고객 원본 변수명 그대로: [[Company_Name]] [[Company_Registration_No]] [[Certificate_No]] [[Number_Of_Shares]]
 *   [[Number_Of_Shares_In_Numeral]] [[Number_Of_Shares_In_Words]] [[Registered_Office_Address]] [[Shareholder_Name]]
 *   [[Date of Issue]] · 서명 2명: [[Director_Name]] / [[Director_Designation]] (이사 1명),
 *   [[Second_Signer_Name]] / [[Second_Signer_Designation]] (이사 또는 비서 1명) — 일반 변수.
 */

const ONES = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'TEN', 'ELEVEN', 'TWELVE',
  'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
const TENS = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];
const SCALES = ['', 'THOUSAND', 'MILLION', 'BILLION', 'TRILLION'];

function under1000(n: number): string {
  const parts: string[] = [];
  const h = Math.floor(n / 100);
  const r = n % 100;
  if (h) parts.push(`${ONES[h]} HUNDRED`);
  if (r) {
    if (h) parts.push('AND');
    if (r < 20) parts.push(ONES[r]);
    else parts.push(TENS[Math.floor(r / 10)] + (r % 10 ? `-${ONES[r % 10]}` : ''));
  }
  return parts.join(' ');
}

/** "100,000" → "ONE HUNDRED THOUSAND" (정수만, 그 외 형식이면 ''). 주식 수 영문 표기용. */
export function numberToWords(v: unknown): string {
  const digits = String(v ?? '').replace(/[,\s]/g, '').trim();
  if (!/^\d+$/.test(digits)) return '';
  let n = Number(digits);
  if (!Number.isFinite(n)) return '';
  if (n === 0) return 'ZERO';
  const chunks: string[] = [];
  let scale = 0;
  while (n > 0 && scale < SCALES.length) {
    const c = n % 1000;
    if (c) chunks.unshift([under1000(c), SCALES[scale]].filter(Boolean).join(' '));
    n = Math.floor(n / 1000);
    scale += 1;
  }
  return chunks.join(' ');
}

/** 화면 키 → 템플릿(고객 원본) 변수명 */
export const TEMPLATE_KEYS: Record<string, string> = {
  company_name: 'Company_Name',
  company_registration_no: 'Company_Registration_No',
  certificate_no: 'Certificate_No',
  number_of_shares: 'Number_Of_Shares',
  number_of_shares_in_numeral: 'Number_Of_Shares_In_Numeral',
  number_of_shares_in_words: 'Number_Of_Shares_In_Words',
  registered_office_address: 'Registered_Office_Address',
  shareholder_name: 'Shareholder_Name',
  date_of_issue: 'Date of Issue',
  // 서명 2명(고객 회신 2026-10-27 · 고객 원본 Share Cert No. 1) — 일반 변수(점선 + 이름 + 직책)
  director_name: 'Director_Name',
  director_designation: 'Director_Designation',
  second_signer_name: 'Second_Signer_Name',
  second_signer_designation: 'Second_Signer_Designation',
};

function isFilled(v: unknown): boolean {
  return String(v ?? '').trim() !== '';
}

/** 주식 수 천 단위 쉼표 — 정수(쉼표·공백 허용)만 "10,000" 으로, 그 외 형식은 그대로 */
export function formatShareCount(v: unknown): string {
  const s = String(v ?? '').trim();
  const digits = s.replace(/[,\s]/g, '');
  if (!/^\d+$/.test(digits)) return s;
  return digits.replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const MONTHS = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
/**
 * Aura 증서 날짜 표기 — 고객 원본(Share Cert No. 1)처럼 "16 JULY 2026" 고정(세션 날짜 형식 미적용).
 *   ISO(YYYY-MM-DD)만 바꾸고, 이미 다른 형식이면 그대로 둔다. (공용 날짜 유틸은 건드리지 않는다)
 */
export function formatAuraCertDate(v: unknown): string {
  const s = String(v ?? '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s;
  const mon = MONTHS[Number(m[2]) - 1];
  return mon ? `${Number(m[3])} ${mon} ${m[1]}` : s;
}

export function normalizeShareCertForWord(form: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = { ...form };
  // 영문 주식수 — 비어 있으면 숫자에서 생성
  if (!isFilled(out.number_of_shares_in_words) && isFilled(out.number_of_shares)) {
    out.number_of_shares_in_words = numberToWords(out.number_of_shares);
  }
  // 본문 숫자([[Number_Of_Shares_In_Numeral]])는 상단 No. of Shares 와 같은 값
  if (!isFilled(out.number_of_shares_in_numeral) && isFilled(out.number_of_shares)) {
    out.number_of_shares_in_numeral = out.number_of_shares;
  }
  // 주식 수 — 문서에는 천 단위 쉼표("10,000", 고객 원본 표기). 영문 표기는 위에서 숫자로 이미 만들었다.
  for (const k of ['number_of_shares', 'number_of_shares_in_numeral']) {
    if (isFilled(out[k])) out[k] = formatShareCount(out[k]);
  }
  // 발행일 — 고객 원본 표기 "16 JULY 2026" (백엔드는 ISO 만 세션 형식으로 바꾸므로 글자로 보낸다)
  if (isFilled(out.date_of_issue)) out.date_of_issue = formatAuraCertDate(out.date_of_issue);
  // 첫 서명자는 항상 이사 — 직책이 비어 있으면 Director
  if (isFilled(out.director_name) && !isFilled(out.director_designation)) out.director_designation = 'Director';
  // 템플릿은 고객 원본 변수명 그대로(사용자 지시 2026-10-27) — 화면 키(소문자) 값을 원본 이름으로도 넣는다.
  //   백엔드 일반 변수는 대소문자를 구분하므로 원본 표기 그대로 + (업로드 시 소문자화 대비) 소문자 표기 둘 다.
  for (const [screenKey, templateKey] of Object.entries(TEMPLATE_KEYS)) {
    if (!isFilled(out[screenKey])) continue;
    out[templateKey] = out[screenKey];
    out[templateKey.toLowerCase()] = out[screenKey];
  }
  return out;
}
